<template>
  <div class="filters-collection">
    <!-- Liste des filtres accordéons -->
    <div class="filters-accordion-container" v-if="hasFilters">
      <!-- Filtres dynamiques triés par position -->
      <div
        v-for="filter in sortedFilters"
        :key="filter.key"
        class="filter-accordion"
      >
        <!-- En-tête de l'accordéon -->
        <div
          class="accordion-header"
          :class="{ active: openAccordions[filter.key] }"
          @click="toggleAccordion(filter.key)"
        >
          <h4 class="accordion-title">
            {{ getFilterLabel(filter.key) }}
            <span
              class="filter-badge"
              v-if="getActiveFilterCount(filter.key) > 0"
            >
              {{ getActiveFilterCount(filter.key) }}
            </span>
          </h4>
          <span
            class="accordion-icon"
            :class="{ open: openAccordions[filter.key] }"
          >
            <svg width="12" height="7" viewBox="0 0 12 7" fill="none">
              <path
                d="M1 1L6 6L11 1"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </span>
        </div>

        <!-- Contenu de l'accordéon -->
        <div class="accordion-content" v-show="openAccordions[filter.key]">
          <!-- Si le filtre est de type prix -->
          <div v-if="filter.key === 'price'">
            <PriceRangeFilter
              :range="priceRange"
              :initial-value="activePriceInCentimes"
              :conversion-factor="100"
              min-label="Min"
              max-label="Max"
              locale="fr-FR"
              currency="EUR"
              @change="handlePriceChange"
            />
          </div>

          <!-- Si le filtre est de type date (stock_date) -->
          <div v-else-if="filter.key === 'stock_date'">
            <DateFilter
              class="date-filter"
              type="stock_date"
              :value1="activeDateFilters.stock_date?.value1 || ''"
              :value2="activeDateFilters.stock_date?.value2 || ''"
              @update="handleDateFilterUpdate"
            />
          </div>

          <!-- Les filtres de couleur utilisent maintenant le template standard des checkboxes -->

          <!-- Filtre par cases à cocher (pour les autres types de filtres) -->
          <div v-else class="checkbox-filter">
            <div
              class="search-filter"
              v-if="getFilterValues(filter.key).length > 10"
            >
              <input
                type="text"
                v-model="filterSearchQueries[filter.key]"
                :placeholder="getSearchPlaceholder(filter.key)"
                class="filter-search-input"
              />
            </div>
            <div
              v-for="(value, index) in filteredValues(filter.key)"
              :key="index"
              class="checkbox-item"
              v-show="shouldShowFilterValue(filter.key, index)"
            >
              <label class="checkbox-label">
                <input
                  type="checkbox"
                  :checked="isFilterValueSelected(filter.key, value)"
                  @change="toggleFilterValue(filter.key, value)"
                />
                <span class="filter-value-label">{{ value }}</span>
                <span
                  class="filter-value-count"
                  v-if="getValueCount(filter.key, value)"
                >
                  {{ getValueCount(filter.key, value) }}
                </span>
              </label>
            </div>
            <div
              class="no-results"
              v-if="filteredValues(filter.key).length === 0"
            >
              Aucun résultat pour "{{ filterSearchQueries[filter.key] }}"
            </div>
            <button
              class="show-more-button"
              v-if="
                showMoreButtons[filter.key] &&
                filteredValues(filter.key).length > maxVisibleOptions
              "
              @click="toggleShowMore(filter.key)"
            >
              {{
                expandedFilters[filter.key]
                  ? "Voir moins"
                  : `Voir plus (${getHiddenValuesCount(filter.key)})`
              }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { debounce } from "lodash";
import PriceRangeFilter from "@/components/ui/dataTable/filters/PriceRangeFilter.vue";
import DateFilter from "@/components/ui/dataTable/filters/DateFilter.vue";
export default {
  name: "FiltersCollection",
  components: { PriceRangeFilter, DateFilter },
  props: {
    // Structure des filtres disponibles (récupérée de l'API all-fields-by-brand)
    filterStructure: {
      type: Object,
      default: () => ({}),
    },
    // Valeurs disponibles pour chaque filtre (récupérées de nextValuesFilter)
    filterValues: {
      type: Object,
      default: () => ({}),
    },
    // Filtres actifs (pour la synchronisation avec le composant parent)
    initialActiveFilters: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      // État des accordéons (ouvert/fermé)
      openAccordions: {},
      // Filtres actifs internes
      activeFilters: {},
      // Recherche pour chaque filtre
      filterSearchQueries: {},
      // Prix actif (pour le filtre de prix)
      activePrice: {
        min: 0,
        max: 5000,
      },
      // Plage de prix (valeurs min et max possibles)
      priceRange: {
        min: 0,
        max: 5000,
      },
      // Filtres étendus (pour afficher tous les éléments)
      expandedFilters: {},
      // Pour suivre l'état du drag and drop du slider de prix
      dragging: null,
      dragStartX: 0,
      dragStartPosition: 0,
      // Positions du range slider
      priceRangePosition: {
        min: 0,
        max: 100,
      },
      // Mode développé pour voir plus d'options
      showMoreButtons: {},
      // Nombre maximum d'options à afficher par défaut
      maxVisibleOptions: 18,
      // colorMap supprimé - les couleurs sont maintenant affichées comme une liste simple
      lastEmittedFilters: null,
      // Filtres de date actifs
      activeDateFilters: {
        stock_date: { value1: "", value2: "" },
      },
    };
  },
  computed: {
    // Vérifier si nous avons des filtres à afficher
    hasFilters() {
      return Object.keys(this.filterStructure).length > 0;
    },

    // Convertir activePrice (en euros) vers les centimes pour PriceRangeFilter
    activePriceInCentimes() {
      const result = {
        min:
          this.activePrice.min !== undefined
            ? this.activePrice.min * 100
            : undefined,
        max:
          this.activePrice.max !== undefined
            ? this.activePrice.max * 100
            : undefined,
      };
      console.log(
        `💰 FiltersCollection: activePriceInCentimes computed - activePrice:`,
        this.activePrice,
        `-> centimes:`,
        result
      );
      return result;
    },

    // Retourner les filtres dans l'ordre de l'API (ordre d'insertion dans l'objet)
    sortedFilters() {
      return Object.entries(this.filterStructure).map(([key, value]) => ({
        key,
        ...value,
      }));
    },
  },
  created() {
    // Initialiser les structures de données
    this.initializeData();

    // Initialiser le debounce pour le filtre de prix
    this.debouncedUpdatePriceFilter = debounce(this.applyPriceFilter, 300);

    // Ajouter les écouteurs d'événements pour le drag des sliders
    window.addEventListener("mousemove", this.handleDrag);
    window.addEventListener("mouseup", this.stopDrag);
    window.addEventListener("touchmove", this.handleDrag);
    window.addEventListener("touchend", this.stopDrag);
  },
  beforeDestroy() {
    // Nettoyer les écouteurs d'événements
    window.removeEventListener("mousemove", this.handleDrag);
    window.removeEventListener("mouseup", this.stopDrag);
    window.removeEventListener("touchmove", this.handleDrag);
    window.removeEventListener("touchend", this.stopDrag);
  },
  watch: {
    // Observer les changements dans la structure des filtres
    filterStructure: {
      handler() {
        this.initializeOpenAccordions();
        this.initializeShowMoreButtons();
      },
      deep: true,
    },

    // Observer les changements dans les valeurs des filtres
    filterValues: {
      handler() {
        this.updatePriceRange();
      },
      deep: true,
    },

    // Observer les changements dans les filtres actifs initiaux
    initialActiveFilters: {
      handler(newFilters) {
        // Mettre à jour les filtres actifs internes
        this.syncActiveFilters(newFilters);
      },
      deep: true,
    },
  },
  methods: {
    // Initialiser les données de base pour les filtres
    initializeData() {
      // Initialiser les filtres actifs depuis les props
      this.syncActiveFilters(this.initialActiveFilters);

      // Initialiser l'état des accordéons
      this.initializeOpenAccordions();

      // Initialiser les recherches par filtre
      this.initializeFilterSearchQueries();

      // Initialiser le filtre de prix
      this.updatePriceRange();

      // Initialiser les boutons "voir plus"
      this.initializeShowMoreButtons();
    },

    // Synchroniser les filtres actifs depuis les props
    syncActiveFilters(newFilters) {
      // Créer un nouvel objet pour éviter les références mutuelles
      this.activeFilters = JSON.parse(JSON.stringify(newFilters || {}));

      // Extraire les filtres de date s'ils existent
      if (newFilters && newFilters.stock_date) {
        this.$set(this.activeDateFilters, "stock_date", {
          value1: newFilters.stock_date.value1 || "",
          value2: newFilters.stock_date.value2 || "",
        });
      } else {
        this.$set(this.activeDateFilters, "stock_date", {
          value1: "",
          value2: "",
        });
      }

      // Extraire les filtres de prix s'ils existent
      if (newFilters && newFilters.price) {
        this.activePrice = {
          min:
            newFilters.price.min !== undefined
              ? Math.round(newFilters.price.min / 100) // Conversion de centimes en euros
              : this.priceRange.min,
          max:
            newFilters.price.max !== undefined
              ? Math.round(newFilters.price.max / 100) // Conversion de centimes en euros
              : this.priceRange.max,
        };
        this.updatePricePositions();
      } else {
        // Si pas de filtre prix (reset), utiliser la vraie plage de prix disponible
        let realPriceRange = { min: 0, max: 5000 }; // Valeurs par défaut

        // 🆕 PRIORITÉ 1 : Essayer d'abord filterValues.price (les vraies données API)
        if (
          this.filterValues &&
          this.filterValues.price &&
          this.filterValues.price.max !== undefined
        ) {
          realPriceRange = {
            min: this.filterValues.price.min || 0,
            max: this.filterValues.price.max,
          };
        }
        // PRIORITÉ 2 : Utiliser la valeur globale
        else if (window.maxPriceValue !== undefined) {
          realPriceRange = {
            min: 0,
            max: window.maxPriceValue,
          };
        }
        // PRIORITÉ 3 : Utiliser priceRange actuel
        else {
          realPriceRange = {
            min: this.priceRange.min,
            max: this.priceRange.max,
          };
        }

        console.log(
          `🔄 FiltersCollection: Reset prix - utilisation de la vraie plage {min: ${realPriceRange.min}, max: ${realPriceRange.max}}`
        );
        this.activePrice = {
          min: realPriceRange.min,
          max: realPriceRange.max,
        };
        this.updatePricePositions();
      }
    },

    // Initialiser l'état des accordéons
    initializeOpenAccordions() {
      const accordionState = {};

      // Parcourir les filtres et définir tous à fermés par défaut
      if (this.filterStructure) {
        Object.keys(this.filterStructure).forEach((key) => {
          // Tous les accordéons fermés par défaut
          accordionState[key] = false;

          // Ouvrir seulement ceux qui ont des valeurs sélectionnées
          // Cas spécial pour le prix : vérifier si un filtre prix existe vraiment dans initialActiveFilters
          if (key === "price") {
            // Ouvrir seulement si un filtre prix est explicitement défini
            if (this.initialActiveFilters && this.initialActiveFilters.price) {
              accordionState[key] = true;
            }
          } else {
            // Pour les autres filtres, utiliser la méthode existante
            if (this.getActiveFilterCount(key) > 0) {
              accordionState[key] = true;
            }
          }
        });
      }

      // Utiliser $set pour rendre réactif
      Object.keys(accordionState).forEach((key) => {
        this.$set(this.openAccordions, key, accordionState[key]);
      });
    },

    // Initialiser les boutons "voir plus"
    initializeShowMoreButtons() {
      const showMoreState = {};

      // Parcourir les filtres et définir les boutons "voir plus" pour ceux ayant trop de valeurs
      Object.keys(this.filterValues).forEach((key) => {
        const values = this.getFilterValues(key);
        if (Array.isArray(values) && values.length > this.maxVisibleOptions) {
          showMoreState[key] = true;
        } else {
          showMoreState[key] = false;
        }
      });

      // Utiliser $set pour rendre réactif
      Object.keys(showMoreState).forEach((key) => {
        this.$set(this.showMoreButtons, key, showMoreState[key]);
      });

      // Initialiser l'état des filtres développés
      Object.keys(showMoreState).forEach((key) => {
        // Conserver l'état précédent s'il existe, sinon initialiser à false
        this.$set(
          this.expandedFilters,
          key,
          this.expandedFilters[key] || false
        );
      });
    },

    // Initialiser les recherches par filtre
    initializeFilterSearchQueries() {
      // Parcourir les filtres et initialiser les recherches
      Object.keys(this.filterValues).forEach((key) => {
        this.$set(this.filterSearchQueries, key, "");
      });
    },

    handlePriceChange({ value, isChanged }) {
      if (isChanged) {
        // Ajouter le filtre de prix aux filtres actifs
        this.$set(this.activeFilters, "price", value);
      } else {
        // Supprimer le filtre si les valeurs sont par défaut
        if (this.activeFilters.price) {
          this.$delete(this.activeFilters, "price");
        }
      }

      // Émettre les changements
      this.emitFiltersChange();
    },

    // Gérer les mises à jour du filtre de date
    handleDateFilterUpdate(type, field, value) {
      // Mettre à jour l'état local
      if (!this.activeDateFilters[type]) {
        this.$set(this.activeDateFilters, type, { value1: "", value2: "" });
      }
      this.$set(this.activeDateFilters[type], field, value);

      const dateFilter = this.activeDateFilters[type];
      const hasValue = dateFilter.value1 || dateFilter.value2;

      if (hasValue) {
        // Ajouter le filtre de date aux filtres actifs
        this.$set(this.activeFilters, type, {
          value1: dateFilter.value1 || "",
          value2: dateFilter.value2 || "",
        });
      } else {
        // Supprimer le filtre si aucune date n'est sélectionnée
        if (this.activeFilters[type]) {
          this.$delete(this.activeFilters, type);
        }
      }

      // Émettre les changements
      this.emitFiltersChange();
    },
    ///////

    // Mettre à jour la plage de prix
    updatePriceRange() {
      // Si nous avons des valeurs de prix dans filterValues
      if (this.filterValues && this.filterValues.price) {
        const priceValues = this.filterValues.price;

        // Si nous avons directement min et max définis (cas attendu après notre modification)
        if (priceValues.min !== undefined && priceValues.max !== undefined) {
          this.priceRange = {
            min: Math.floor(parseFloat(priceValues.min)),
            max: Math.ceil(parseFloat(priceValues.max)), // Arrondi au-dessus pour être sûr
          };
          console.log(
            `FiltersCollection: Prix mis à jour depuis filterValues: min=${this.priceRange.min}, max=${this.priceRange.max}`
          );
        }
        // Le reste de votre code pour les autres cas...
        else if (Array.isArray(priceValues) && priceValues.length > 0) {
          // Trouver les valeurs min et max dans le tableau
          const numericValues = priceValues
            .map((v) => parseFloat(v))
            .filter((v) => !isNaN(v));

          if (numericValues.length > 0) {
            this.priceRange = {
              min: Math.floor(Math.min(...numericValues)),
              max: Math.ceil(Math.max(...numericValues)),
            };
          }
        }

        // S'assurer que la plage a une valeur minimale
        if (this.priceRange.min === this.priceRange.max) {
          this.priceRange.min = 0;
        }

        // Initialiser les prix actifs si nécessaire
        if (
          !this.activePrice ||
          this.activePrice.min === undefined ||
          this.activePrice.max === undefined
        ) {
          this.activePrice = {
            min: this.priceRange.min,
            max: this.priceRange.max,
          };
        }

        // Mettre à jour les positions du slider
        this.updatePricePositions();
      }
      // Fallback: utiliser la valeur globale si pas de filterValues.price
      else if (window.maxPriceValue !== undefined) {
        this.priceRange = {
          min: 0,
          max: window.maxPriceValue,
        };

        // Initialiser les prix actifs avec la plage complète
        if (
          !this.activePrice ||
          this.activePrice.min === undefined ||
          this.activePrice.max === undefined
        ) {
          this.activePrice = {
            min: this.priceRange.min,
            max: this.priceRange.max,
          };
        }

        // Mettre à jour les positions du slider
        this.updatePricePositions();

        console.log(
          `FiltersCollection: Prix mis à jour depuis valeur globale: min=${this.priceRange.min}, max=${this.priceRange.max}`
        );
      }
    },

    // Mettre à jour les positions du slider de prix
    updatePricePositions() {
      const range = this.priceRange.max - this.priceRange.min;
      if (range === 0) return; // Éviter la division par zéro

      const minPos =
        ((this.activePrice.min - this.priceRange.min) / range) * 100;
      const maxPos =
        ((this.activePrice.max - this.priceRange.min) / range) * 100;

      this.priceRangePosition = {
        min: Math.max(0, Math.min(100, minPos)),
        max: Math.max(0, Math.min(100, maxPos)),
      };
    },

    // Basculer l'état d'un accordéon
    toggleAccordion(key) {
      this.$set(this.openAccordions, key, !this.openAccordions[key]);
    },

    // Obtenir l'étiquette d'un filtre
    getFilterLabel(key) {
      let label = "";

      // Si le filtre a une étiquette personnalisée dans la structure
      if (this.filterStructure[key] && this.filterStructure[key].label) {
        label = this.filterStructure[key].label;
      } else {
        label = this.$t(`filters.labels.${key}`) || key;
      }

      // S'assurer que le label commence toujours par une majuscule
      return label.charAt(0).toUpperCase() + label.slice(1);
    },

    // Obtenir les valeurs d'un filtre
    getFilterValues(key) {
      if (this.filterValues && this.filterValues[key]) {
        let values = [];
        if (Array.isArray(this.filterValues[key])) {
          values = this.filterValues[key];
        } else if (
          this.filterValues[key].values &&
          Array.isArray(this.filterValues[key].values)
        ) {
          values = this.filterValues[key].values;
        }

        // Normalisation supprimée pour les couleurs - utilisation du format standard

        return values;
      }
      return [];
    },

    // Méthode normalizeColorValues supprimée

    // Filtrer les valeurs en fonction de la recherche
    filteredValues(key) {
      const values = this.getFilterValues(key);
      const searchQuery = this.filterSearchQueries[key];

      if (!searchQuery) return values;

      return values.filter((value) =>
        String(value).toLowerCase().includes(searchQuery.toLowerCase())
      );
    },

    // Obtenir le nombre d'éléments pour une valeur de filtre
    getValueCount(key, value) {
      // Si nous avons des compteurs dans les données de filtre
      if (this.filterValues[key] && this.filterValues[key].counts) {
        return this.filterValues[key].counts[value] || 0;
      }
      return null;
    },

    // Obtenir le nombre de filtres actifs pour une clé donnée
    getActiveFilterCount(key) {
      if (key === "price") {
        // Vérifier si le filtre de prix est actif
        return this.activePrice.min > this.priceRange.min ||
          this.activePrice.max < this.priceRange.max
          ? 1
          : 0;
      }

      // Vérifier si c'est un filtre de date
      if (key === "stock_date") {
        const dateFilter = this.activeDateFilters[key];
        return dateFilter && (dateFilter.value1 || dateFilter.value2) ? 1 : 0;
      }

      // Pour les autres filtres, compter les valeurs sélectionnées
      if (this.activeFilters[key] && Array.isArray(this.activeFilters[key])) {
        return this.activeFilters[key].length;
      }

      return 0;
    },

    // Vérifier si une valeur de filtre est sélectionnée
    isFilterValueSelected(key, value) {
      return (
        this.activeFilters[key] &&
        Array.isArray(this.activeFilters[key]) &&
        this.activeFilters[key].includes(value)
      );
    },

    // MÉTHODE CORRIGÉE : Basculer une valeur de filtre
    toggleFilterValue(key, value) {
      console.log(
        `🎯 FiltersCollection: toggleFilterValue - key: ${key}, value: ${value}`
      );

      // Vérifier si le filtre existe déjà
      if (!this.activeFilters[key]) {
        console.log(`📌 Création du tableau pour ${key}`);
        // Utiliser $set pour créer un nouveau tableau réactif
        this.$set(this.activeFilters, key, []);
      }

      // Créer une copie du tableau actuel pour éviter la mutation directe
      const updatedValues = [...this.activeFilters[key]];
      const index = updatedValues.indexOf(value);

      if (index === -1) {
        // Ajouter la valeur
        updatedValues.push(value);
        console.log(`➕ Ajout de la valeur "${value}" pour ${key}`);
      } else {
        // Retirer la valeur
        updatedValues.splice(index, 1);
        console.log(`➖ Suppression de la valeur "${value}" pour ${key}`);
      }

      // Remplacer le tableau complet plutôt que de le modifier
      this.$set(this.activeFilters, key, updatedValues);

      // Si le tableau devient vide, le supprimer
      if (this.activeFilters[key].length === 0) {
        console.log(`🗑️ Suppression de la clé ${key} (tableau vide)`);
        this.$delete(this.activeFilters, key);
      }

      console.log(
        `📤 État final des filtres:`,
        JSON.stringify(this.activeFilters, null, 2)
      );

      // Émettre les changements avec une copie profonde
      this.emitFiltersChange();
    },

    // Déterminer si une valeur de filtre doit être affichée
    shouldShowFilterValue(key, index) {
      // Si le filtre est développé, montrer toutes les valeurs
      if (this.expandedFilters[key]) {
        return true;
      }

      // Sinon, montrer seulement les premières valeurs
      return index < this.maxVisibleOptions;
    },

    // Basculer l'état développé d'un filtre
    toggleShowMore(key) {
      this.$set(this.expandedFilters, key, !this.expandedFilters[key]);
    },

    // Obtenir le nombre de valeurs cachées
    getHiddenValuesCount(key) {
      // Utiliser filteredValues pour tous les types de filtres maintenant
      const values = this.filteredValues(key);
      const hiddenCount = values.length - this.maxVisibleOptions;
      return Math.max(0, hiddenCount);
    },

    // Gérer le début du drag pour le slider de prix
    startDrag(handle, event) {
      // Utiliser le bon événement selon le type
      const clientX = event.type.startsWith("touch")
        ? event.touches[0].clientX
        : event.clientX;

      this.dragging = handle;
      this.dragStartX = clientX;
      this.dragStartPosition = this.priceRangePosition[handle];

      // Empêcher les événements par défaut
      event.preventDefault();
      event.stopPropagation();
    },

    // Gérer le drag du slider de prix
    handleDrag(event) {
      if (!this.dragging) return;

      // Utiliser le bon événement selon le type
      const clientX = event.type.startsWith("touch")
        ? event.touches[0].clientX
        : event.clientX;

      const sliderEl = document.querySelector(".price-range-slider");
      if (!sliderEl) return;

      const rect = sliderEl.getBoundingClientRect();
      const width = rect.width;

      // Calculer le déplacement relatif en pourcentage
      const deltaX = clientX - this.dragStartX;
      const deltaPercent = (deltaX / width) * 100;

      // Nouvelle position
      let newPosition = this.dragStartPosition + deltaPercent;

      // Limiter la position entre 0 et 100
      newPosition = Math.max(0, Math.min(100, newPosition));

      // Mettre à jour la position selon la poignée en cours de drag
      if (this.dragging === "min") {
        // Empêcher min de dépasser max
        newPosition = Math.min(newPosition, this.priceRangePosition.max - 5);
        this.$set(this.priceRangePosition, "min", newPosition);
      } else if (this.dragging === "max") {
        // Empêcher max de passer sous min
        newPosition = Math.max(newPosition, this.priceRangePosition.min + 5);
        this.$set(this.priceRangePosition, "max", newPosition);
      }

      // Mettre à jour les valeurs de prix actives
      this.updateActivePriceFromPositions();
    },

    // Arrêter le drag
    stopDrag() {
      if (this.dragging) {
        this.dragging = null;
        this.applyPriceFilter();
      }
    },

    // Mettre à jour les prix actifs depuis les positions
    updateActivePriceFromPositions() {
      const range = this.priceRange.max - this.priceRange.min;

      this.activePrice = {
        min: Math.round(
          this.priceRange.min + (this.priceRangePosition.min * range) / 100
        ),
        max: Math.round(
          this.priceRange.min + (this.priceRangePosition.max * range) / 100
        ),
      };
    },

    // Mettre à jour le filtre de prix suite à une modification de l'input
    updatePriceFilter() {
      // Corriger les valeurs si nécessaire
      this.activePrice.min = Math.max(
        this.priceRange.min,
        Math.min(this.activePrice.min, this.activePrice.max - 1)
      );
      this.activePrice.max = Math.max(
        this.activePrice.min + 1,
        Math.min(this.activePrice.max, this.priceRange.max)
      );

      // Mettre à jour les positions du slider
      this.updatePricePositions();

      // Appliquer les changements avec debounce
      this.debouncedUpdatePriceFilter();
    },

    // Appliquer le filtre de prix
    applyPriceFilter() {
      // Ajouter le filtre de prix aux filtres actifs
      const isPriceChanged =
        this.activePrice.min > this.priceRange.min ||
        this.activePrice.max < this.priceRange.max;

      if (isPriceChanged) {
        this.$set(this.activeFilters, "price", {
          min: Math.round(this.activePrice.min * 100), // Conversion en centimes
          max: Math.round(this.activePrice.max * 100),
        });
      } else if (this.activeFilters.price) {
        this.$delete(this.activeFilters, "price");
      }

      // Émettre les changements
      this.emitFiltersChange();
    },

    // Émettre les changements de filtres au parent
    emitFiltersChange() {
      // CORRECTION: Vérifier si les filtres ont changé avant d'émettre l'événement
      const newFilters = JSON.parse(JSON.stringify(this.activeFilters));

      // Ajouter une propriété timestamp pour éviter les cycles de mise à jour
      newFilters._timestamp = Date.now();

      // Si les filtres sont vides et que nous n'avions pas de filtres avant, ne rien faire
      if (Object.keys(newFilters).length === 0 && !this.lastEmittedFilters) {
        return;
      }

      // Si les filtres sont identiques à ceux déjà émis, ne rien faire
      const currentFiltersString = JSON.stringify(
        this.lastEmittedFilters || {}
      );
      const newFiltersString = JSON.stringify(newFilters);

      if (currentFiltersString === newFiltersString) {
        return;
      }

      // Stocker les derniers filtres émis
      this.lastEmittedFilters = newFilters;

      // Émettre l'événement
      this.$emit("filters-change", newFilters);
    },

    // Formater un prix
    formatPrice(price) {
      return new Intl.NumberFormat("fr-FR", {
        style: "currency",
        currency: "EUR",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(price);
    },

    // Méthode getColorBackground supprimée

    // Obtenir le placeholder de recherche adapté au type de filtre
    getSearchPlaceholder(key) {
      switch (key) {
        case "brand":
          return this.$t("article_put_online.placeholder_search_brand");
        case "color":
        case "color_filters":
          return this.$t("article_put_online.placeholder_search_color");
        default:
          return `Rechercher dans ${this.getFilterLabel(key)}`;
      }
    },
  },
};
</script>

<style scoped>
.filters-collection {
  width: 100%;
  padding: 8px 0;
}

/* Conteneur d'accordéons */
.filters-accordion-container {
  display: flex;
  flex-direction: column;
}

/* Accordéon */
.filter-accordion {
  border-bottom: 1px solid #eaeaea;
}

.accordion-header {
  padding: 12px 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  gap: 16px;
  transition: all 0.2s ease;
}

.accordion-header:hover {
  background-color: #f9f9f9;
}

.accordion-header.active {
  background-color: #f5f5f5;
}

.accordion-title {
  font-size: 14px;
  font-weight: 500;
  margin: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  width: 100%;
}

.filter-badge {
  background-color: #007bff;
  color: white;
  border-radius: 10px;
  padding: 2px 6px;
  font-size: 10px;
  font-weight: 600;
  min-width: 18px;
  text-align: center;
}

.accordion-icon {
  transition: transform 0.3s ease;
}

.accordion-icon.open {
  transform: rotate(180deg);
}

/* Contenu de l'accordéon */
.accordion-content {
  padding: 16px;
  overflow: visible;
}

/* Filtre de recherche */
.search-filter {
  margin-bottom: 12px;
}

.filter-search-input {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  font-size: 14px;
}

/* Filtre de prix */
.price-filter {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.price-range-wrapper {
  width: 100%;
  padding: 0 8px;
}

.price-range-indicators {
  display: flex;
  justify-content: space-between;
  margin-bottom: 12px;
  font-size: 14px;
  color: #666;
}

.price-range-slider {
  position: relative;
  height: 24px;
  margin-bottom: 8px;
}

.range-track {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 100%;
  height: 4px;
  background-color: #e0e0e0;
  border-radius: 2px;
}

.range-progress {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  height: 4px;
  background-color: #000;
  border-radius: 2px;
}

.range-handle {
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 16px;
  height: 16px;
  background-color: white;
  border: 2px solid #000;
  border-radius: 50%;
  cursor: pointer;
  z-index: 1;
  transition: transform 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

.range-handle:hover {
  transform: translate(-50%, -50%) scale(1.1);
}

.price-inputs {
  display: flex;
  gap: 10px;
}

.price-input-group {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.price-input-group label {
  font-size: 12px;
  color: #666;
}

.price-input-group input {
  width: 100%;
  padding: 8px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  font-size: 13px;
}

/* Styles des pastilles de couleur supprimés - les couleurs utilisent maintenant le style standard des checkboxes */

/* Filtre à cases à cocher */
.checkbox-filter {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.checkbox-item {
  margin-bottom: 2px;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  cursor: pointer;
}

.checkbox-label input {
  width: 16px;
  height: 16px;
  cursor: pointer;
}

.filter-value-label {
  flex: 1;
}

.filter-value-count {
  font-size: 12px;
  color: #888;
  background-color: #f5f5f5;
  padding: 1px 6px;
  border-radius: 10px;
}

/* Bouton "voir plus" */
.show-more-button {
  background: none;
  border: none;
  text-align: left;
  font-size: 13px;
  color: #666;
  cursor: pointer;
  padding: 4px 0;
  margin-top: 8px;
  text-decoration: underline;
}

.show-more-button:hover {
  color: #000;
}

/* Message "aucun résultat" */
.no-results {
  padding: 12px 0;
  color: #888;
  font-style: italic;
  font-size: 14px;
  text-align: center;
}

/* Personnalisation des inputs */
input[type="checkbox"] {
  accent-color: #000;
}

input[type="text"],
input[type="number"] {
  outline: none;
  transition: border-color 0.2s ease;
}

input[type="text"]:focus,
input[type="number"]:focus {
  border-color: #000;
}

/* Cache les flèches sur les inputs numériques */
input[type="number"]::-webkit-outer-spin-button,
input[type="number"]::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

input[type="number"] {
  -moz-appearance: textfield;
  appearance: textfield;
}

.date-filter.filter-group {
  padding: 0;
}
.date-filter.filter-group .filter-group-header {
  display: none;
}
/* Styles spécifiques des swatches supprimés */

/* Media queries pour la responsive */
@media (max-width: 768px) {
  .price-inputs {
    flex-direction: row;
  }
}
</style>
